/**
 * Сброс кеша по пачке подтверждений, а не по каждому.
 *
 * 12.10.2026: вкладка с кампанией CTMOBI, где человек включал и ставил на
 * паузу десятки ключей, за три минуты отправила 1 800 запросов. Сливщик
 * подтверждает заявки по одной, опрос раз в 1,5 с почти каждый раз что-то
 * находил и сбрасывал кеш, и каждый сброс перезапрашивал шапку кампании,
 * группы и ключи. Под нагрузкой такой запрос идёт 3–30 с, а новый
 * отправлялся поверх незакончившегося: браузер бросает старый, сервер
 * продолжает его считать. Пул соединений API кончился (Sentry
 * ONEADS-API-KN/KQ/10D).
 *
 * Правило: виды копятся, сброс — не чаще раза в `gapMs`, и только когда ни
 * один запрос этих видов не идёт; иначе ждём следующего окна. Последнее
 * подтверждение не теряется: отложенные виды сбрасываются, как только
 * запросы закончились.
 */
export type CoalescedInvalidator<T> = {
  add: (types: Iterable<T>) => void;
  /** Для тестов и размонтирования. */
  cancel: () => void;
};

export function createCoalescedInvalidator<T>(opts: {
  flush: (types: ReadonlySet<T>) => void;
  /** Идёт ли сейчас запрос, который сброс этих видов перезапустил бы. */
  isBusy: (types: ReadonlySet<T>) => boolean;
  gapMs: number;
  now?: () => number;
}): CoalescedInvalidator<T> {
  const now = opts.now ?? (() => Date.now());
  let pending = new Set<T>();
  let lastFlush = -Infinity;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const arm = (delay: number) => {
    timer = setTimeout(run, Math.max(0, delay));
  };

  const run = () => {
    timer = null;
    if (pending.size === 0) return;
    if (opts.isBusy(pending)) {
      arm(opts.gapMs);
      return;
    }
    const types = pending;
    pending = new Set();
    lastFlush = now();
    opts.flush(types);
  };

  return {
    add(types) {
      for (const t of types) pending.add(t);
      if (pending.size === 0 || timer !== null) return;
      arm(lastFlush + opts.gapMs - now());
    },
    cancel() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending = new Set();
    },
  };
}
